import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";

import { debounce } from "../../utils/helpers";
import api from "../../api";

dayjs.extend(calendar);

const state = () => ({
  history: [], // { id, content, author, datetime, received }
  showInputAnswer: false,
  choices: [], // { id, text, displayText }
  inputAnswer: "",
  stickDates: false,
  sendingMessages: [], // { tempId, content, has_error }
  loadingOld: false
});

const getters = {
  showHorizontalChoices: state => {
    if (!state.choices.length) return false;
    const textIsLong = choice => choice.displayText.length > 10;
    const hasLongText = state.choices.filter(textIsLong).length;
    return !hasLongText;
  },
  showVerticalChoices: (state, getters) => {
    if (!state.choices.length) return false;
    return !getters.showHorizontalChoices;
  },
  orderedHistory: state =>
    [...state.history].sort((a, b) => a.datetime - b.datetime),
  lastMessage: (_, getters) => getters.orderedHistory.slice(-1)[0],
  firstMessage: (_, getters) => getters.orderedHistory[0],
  firstMessagesByDay: (_, getters) => {
    const messages = getters.orderedHistory;
    let lastDatetime;
    let filteredDates = {};

    for (const m of messages) {
      if (!lastDatetime || !m.datetime.isSame(lastDatetime, "day")) {
        filteredDates[m.id] = m.datetime.format("YYYYMMDD");
        lastDatetime = m.datetime;
      }
    }
    return filteredDates;
  }
};

const mutations = {
  addMessages(state, messages) {
    const historyIds = state.history.map(({ id }) => id);
    const newMessages = messages.filter(
      ({ id }) => historyIds.indexOf(id) === -1
    );
    state.history.push(...newMessages);
  },
  addSendingMessages(state, sendingMessages) {
    state.sendingMessages.push(...sendingMessages);
  },
  updateSendingMessage(state, { tempId, ...data }) {
    const index = state.sendingMessages.findIndex(m => m.tempId == tempId);
    const message = { ...state.sendingMessages[index], ...data };
    state.sendingMessages[index] = message;
  },
  clearSendingMessages(state) {
    state.sendingMessages = [];
  },
  waitForAnswer(state, { choices = [], showInput = false }) {
    state.showInputAnswer = !!showInput;
    choices = Array.isArray(choices) && choices.length ? choices : [];
    choices = choices.map(choice => {
      const displayText = choice.text.replace(/^Contém\s*/i, "");
      return { ...choice, displayText };
    });
    state.choices = choices;
  },
  updateInputAnswer(state, answer = "") {
    state.inputAnswer = answer;
  },
  clearAnswers(state) {
    state.showInputAnswer = false;
    state.inputAnswer = "";
    state.choices = [];
  },
  updateStickies(state, scrolling = false) {
    state.stickDates = scrolling;
  },
  updateLoadingOld(state, loadingOld) {
    state.loadingOld = loadingOld;
  }
};

const actions = {
  async loadNewMessages({ commit, state, getters }) {
    let messages = await api.listHistory({
      ascending: true,
      lastMessageId: getters.lastMessage?.id
    });
    if (messages.length == 0) {
      if (state.history.length === 0) {
        commit("waitForAnswer", { showInput: true });
      }
      return;
    }
    messages = messages.map(m => ({
      id: m.id,
      content: m.texto,
      datetime: dayjs.unix(m.timestamp),
      author: m.origem,
      received: m.origem === "CleverCare"
    }));
    commit("addMessages", messages);
    commit("clearSendingMessages");

    const lastMessage = getters.lastMessage;
    if (!lastMessage.received) {
      commit("waitForAnswer", { showInput: true });
      return;
    }
    let choices = await api.getChoices({ messageId: lastMessage.id });
    if (!choices || !Array.isArray(choices)) {
      commit("waitForAnswer", { showInput: true });
      return;
    }
    choices = choices.map(choice => ({
      id: choice.id,
      text: choice.texto || choice["0"]
    }));
    commit("waitForAnswer", { choices });
  },
  async loadOldMessages({ commit, getters }) {
    commit("updateLoadingOld", true);
    const firstMessage = getters.firstMessage;
    if (!firstMessage) {
      return;
    }
    let messages = await api.listHistory({
      lastMessageId: firstMessage.id
    });
    if (messages.length == 0) {
      return;
    }
    messages = messages.map(m => ({
      id: m.id,
      content: m.texto,
      datetime: dayjs.unix(m.timestamp),
      author: m.origem,
      received: m.origem === "CleverCare"
    }));
    commit("addMessages", messages);
    commit("updateLoadingOld", false);
  },
  async sendInputAnswer({ commit, state, dispatch }) {
    const inputAnswer = state.inputAnswer;
    if (inputAnswer === "") return;

    const tempId = "temp" + dayjs().unix();
    commit("clearAnswers");
    commit("addSendingMessages", [{ tempId, content: inputAnswer }]);
    try {
      await api.sendTextAnswer({ text: inputAnswer });
    } catch (error) {
      commit("updateSendingMessage", { tempId, hasError: true });
      commit("waitForAnswer", { showInput: true });
    }
    dispatch("loadNewMessages");
  },
  async sendChoiceAnswer({ commit, getters, dispatch }, choice) {
    const tempId = "temp_" + dayjs().unix();
    commit("clearAnswers");
    commit("addSendingMessages", [{ tempId, content: choice.displayText }]);
    try {
      await api.sendChoiceAnswer({
        messageId: getters.lastMessage.id,
        choiceId: choice.id,
        choiceText: choice.text
      });
    } catch (error) {
      commit("updateSendingMessage", { tempId, hasError: true });
      commit("waitForAnswer", { showInput: true });
    }
    dispatch("loadNewMessages");
  },
  async onScroll({ commit, state, dispatch }, event) {
    const el = event.target;
    commit("updateStickies", true);
    dispatch("hideStickes");

    if (!state.loadingOld && el.scrollTop < 100) {
      dispatch("loadOldMessages");
    }
    if (el.scrollTop < 1) {
      // scrollTop = 1 prevents scroll to top when old messages appear
      el.scrollTop = 1;
    }
  },
  hideStickes: debounce(({ commit }) => commit("updateStickies", false), 3000)
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
